/** @format */

import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponse } from "axios";
import { ISelectOption, patients, scheduling } from "lobbie";
import {
    BaseSyntheticEvent,
    ReactNode,
    createElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FiChevronRight } from "react-icons/fi";
import { EUserCondition } from "src/components/patients/scheduling/constants";
import PatientSchedulingUserDetailsChildForm from "src/components/patients/scheduling/v2/PatientSchedulingUserDetailsChildForm";
import PatientSchedulingUserDetailsChildren from "src/components/patients/scheduling/v2/PatientSchedulingUserDetailsChildren";
import LobbieCheckboxV2 from "src/components/shared/LobbieCheckboxV2";
import FullScreenLoadingSpinner from "src/components/shared/loading/FullScreenLoadingSpinner";
import NewPatientFormFields from "src/components/shared/patients/NewPatientFormFields";
import { IFormData } from "src/components/shared/patients/constants";
import { ACCOUNT_PUBLIC_UUID, BASE_PUBLIC_API_ROUTE, EUserType } from "src/constants";
import { isSendMethodEmail, isSendMethodSMS } from "src/constants/notifications";
import { EPatientIntakeMethod } from "src/constants/patients";
import { usePatientSchedulingAccountPatient } from "src/hooks/patient_scheduling/usePatientSchedulingAccountPatient";
import { usePatientSelfSchedulingContext } from "src/hooks/patient_scheduling/usePatientSelfSchedulingContext";
import { useLobbiePatient } from "src/hooks/patients/useLobbiePatient";
import { useAxiosPublicGet } from "src/hooks/useAxios";
import {
    handleError,
    isEmailAutoGenerated,
    isEmptyObject,
    isNumeric,
    logDev,
    notify,
    removeNonDigits,
} from "src/utils";
import { PATIENT_SCHEDULING_PATIENT_DETAILS_SCHEMA } from "src/utils/validations/patient";

interface IProps {
    title?: ReactNode;
    handleBack?: () => void;
    handlePatientCreateOrUpdateResponseData: (result: patients.IPatientAccountPatient) => void;
    handlePatientChildrenCreateOrUpdateResponseData: (
        result: scheduling.patient.IAccountPatientSelectedAccountPatient,
    ) => void;
}

const toOption = (label: string) => {
    return {
        value: label,
        label: label,
    };
};

const PatientSchedulingUserDetailsForm: React.FC<IProps> = ({
    title,
    handlePatientCreateOrUpdateResponseData,
    handlePatientChildrenCreateOrUpdateResponseData,
}) => {
    const getter = useAxiosPublicGet();
    const { selfSchedule, selfScheduleSettings, setSelfSchedule } =
        usePatientSelfSchedulingContext();
    // if (!selfSchedule || !selfScheduleSettings) return null;

    const verificationMethod = selfScheduleSettings?.verificationMethod;
    const isVerifyDOB = selfScheduleSettings?.verifyDOBOnFormsOpen ?? false;

    const patientFieldName = useMemo(
        () => selfScheduleSettings?.patientFieldName || "Patient",
        [selfScheduleSettings?.patientFieldName],
    );
    const lowerPatientFieldName = useMemo(
        () => patientFieldName?.toLowerCase(),
        [patientFieldName],
    );

    const [user] = useLobbiePatient(selfScheduleSettings?.accountPublicUuid);
    const { createOrUpdatePatientAccountPatient, createOrUpdatePatientChildren } =
        usePatientSchedulingAccountPatient();

    const [isAddingChild, setAddingChild] = useState<boolean>(false);
    const handleAddingChild = useCallback(() => setAddingChild(true), []);
    const handleNotAddingChild = useCallback(() => setAddingChild(false), []);

    const [relationshipOptions, setRelationshipOptions] = useState<ISelectOption[]>([]);

    const [accountPatientChildren, setAccountPatientChildren] = useState<
        scheduling.patient.IPatientDetailsChild[]
    >(getDefaultChildren(selfSchedule?.accountPatient || null));
    const selectedChildIndex = useMemo(
        () => accountPatientChildren.findIndex((c) => c.isSelected),
        [accountPatientChildren],
    );

    const [isBookingForChild, setBookingForChild] = useState<boolean>(
        !isEmptyObject(accountPatientChildren),
    );
    const handleSetBookingForChild = useCallback(() => {
        setBookingForChild((current) => !current);
    }, []);

    const [isLoading, setLoading] = useState<boolean>(false);

    const userCondition = useMemo(() => {
        if (!isEmptyObject(selfSchedule?.accountPatient)) {
            return EUserCondition.ExistingAccountPatient;
        } else if (!isEmptyObject(user)) {
            return EUserCondition.ExistingPatient;
        } else {
            return EUserCondition.NewUser;
        }
    }, [selfSchedule?.accountPatient, user]);

    const defaultValues = useMemo(() => {
        if (
            userCondition === EUserCondition.ExistingAccountPatient &&
            selfSchedule?.accountPatient
        ) {
            return {
                firstName: selfSchedule.accountPatient.firstName,
                lastName: selfSchedule.accountPatient.lastName,
                dateOfBirth: selfSchedule.accountPatient.dateOfBirth,
                email:
                    selfSchedule.email && !isEmailAutoGenerated(selfSchedule.email)
                        ? selfSchedule.email
                        : isEmailAutoGenerated(selfSchedule.accountPatient.email)
                          ? ""
                          : (selfSchedule.accountPatient.email ?? ""),
                mobilePhone:
                    removeNonDigits(selfSchedule.phone) ||
                    selfSchedule.accountPatient.mobilePhone ||
                    "",
                children: selfSchedule.accountPatient.children || [],
                accountPatientId: selfSchedule.accountPatient.accountPatientId,
            };
        } else if (userCondition === EUserCondition.ExistingPatient && user) {
            return {
                firstName: user.firstName,
                lastName: user.lastName,
                dateOfBirth: user.accountPatient?.dateOfBirth || "",
                email:
                    selfSchedule?.email && !isEmailAutoGenerated(selfSchedule.email)
                        ? selfSchedule.email
                        : isEmailAutoGenerated(user.email)
                          ? ""
                          : (user.email ?? ""),
                mobilePhone: removeNonDigits(selfSchedule?.phone) || user.mobilePhone || "",
                children: [],
            };
        } else {
            return {
                firstName: "",
                lastName: "",
                dateOfBirth: "",
                email: selfSchedule?.email,
                mobilePhone: removeNonDigits(selfSchedule?.phone || "") || "",
                children: [],
            };
        }
    }, [
        userCondition,
        selfSchedule?.accountPatient,
        user,
        selfSchedule?.email,
        selfSchedule?.phone,
    ]);

    const {
        register,
        control,
        getValues,
        setValue,
        handleSubmit,
        formState: { errors },
    } = useForm<any>({
        resolver: yupResolver(PATIENT_SCHEDULING_PATIENT_DETAILS_SCHEMA),
        defaultValues,
    });

    if (!isEmptyObject(errors)) {
        logDev("PatientSchedulingUserDetailsForm.errors -", errors);
    }

    const onSubmitNewPatient = useCallback(
        async (
            form: IFormData,
        ): Promise<patients.IAccountPatient | patients.IPatientAccountPatient | void> => {
            if (!selfSchedule) {
                return;
            }
            if (isVerifyDOB && !form.dateOfBirth) {
                notify({ level: "warning", title: "Date of birth is required." });
                return;
            }
            if (verificationMethod && isNumeric(verificationMethod)) {
                const isVerifyEmail = isSendMethodEmail(verificationMethod);
                const isVerifySMS = isSendMethodSMS(verificationMethod);
                if (isVerifyEmail && !form.email) {
                    notify({ level: "warning", title: "Email is required." });
                    return;
                }
                if (isVerifySMS && !form.mobilePhone) {
                    notify({ level: "warning", title: "Phone is required." });
                    return;
                }
            }
            if (
                form.email &&
                (!selfSchedule?.isEmailConfirmed || form.email !== selfSchedule.email)
            ) {
                setSelfSchedule({ ...selfSchedule, email: form.email, isEmailConfirmed: false });
                //return;
            } else if (
                form.mobilePhone &&
                (!selfSchedule.isPhoneConfirmed || form.mobilePhone !== selfSchedule.phone)
            ) {
                setSelfSchedule({
                    ...selfSchedule,
                    phone: removeNonDigits(form.mobilePhone),
                    isPhoneConfirmed: false,
                });
            }

            setLoading(true);

            const data = {
                ...form,
                email: selfSchedule.email || form.email, // don't allow self-scheduling user to change email if present (is confirmed)
                mobilePhone: removeNonDigits(selfSchedule.phone || form.mobilePhone), // don't allow self-scheduling user to change phone if present (is confirmed)
                receiveSms: !isEmptyObject(removeNonDigits(selfSchedule.phone || form.mobilePhone)),
                receiveEmail: true,
                intakeMethod: EPatientIntakeMethod.PATIENT_SCHEDULED_APPOINTMENT,
                isPhoneConfirmed: selfSchedule.isPhoneConfirmed,
                isEmailConfirmed: selfSchedule.isEmailConfirmed,
                trackingUuid: selfScheduleSettings?.trackingUuid,
            };

            return createOrUpdatePatientAccountPatient(
                { ...data },
                userCondition !== EUserCondition.NewUser,
            )
                .then((result) => {
                    result && !isBookingForChild && handlePatientCreateOrUpdateResponseData(result);
                    return result;
                })
                .then((result) => {
                    if (!result?.accountPatient) {
                        logDev(
                            "PatientSchedulingUserDetailsForm.createOrUpdatePatientAccountPatient - no result. Skip updating child patients",
                            { result },
                        );
                        setLoading(false);
                        return;
                    }
                    const parentAccountPatientId =
                        result?.accountPatient &&
                        (result.accountPatient?.accountPatientId || result.accountPatient?.id);

                    if (!parentAccountPatientId) {
                        notify({
                            level: "error",
                            title: `Failed to update/create the your profile, please try again.`,
                        });
                        setLoading(false);
                    } else if (isBookingForChild) {
                        createOrUpdatePatientChildren(
                            accountPatientChildren,
                            parentAccountPatientId,
                        )
                            .then((childrenResult) => {
                                setLoading(false);

                                logDev("PatientSchedulingUserDetailsForm.childrenResult", {
                                    childrenResult,
                                });
                                if (childrenResult) {
                                    handlePatientChildrenCreateOrUpdateResponseData(childrenResult);
                                }
                            })
                            .catch((error) => {
                                setLoading(false);
                                handleError(error);
                            });
                    } else {
                        setLoading(false);
                        const { accountPatient: newAccountPatient } = result;
                        setSelfSchedule({
                            ...selfSchedule,
                            selectedAccountPatient: newAccountPatient,
                            email: isEmailAutoGenerated(newAccountPatient.email)
                                ? null
                                : newAccountPatient.email,
                            phone: newAccountPatient.mobilePhone,
                        });
                    }
                })
                .catch((error) => {
                    setLoading(false);
                    handleError(error);
                });
        },
        [
            selfSchedule,
            isVerifyDOB,
            verificationMethod,
            selfScheduleSettings?.trackingUuid,
            createOrUpdatePatientAccountPatient,
            userCondition,
            setSelfSchedule,
            isBookingForChild,
            handlePatientCreateOrUpdateResponseData,
            createOrUpdatePatientChildren,
            accountPatientChildren,
            handlePatientChildrenCreateOrUpdateResponseData,
        ],
    );

    const representativeFormTitle = useMemo(
        () =>
            isBookingForChild ? (
                <div className="col mb-3">
                    <div className="row">
                        <h5 className="extra-bold col">Your details:</h5>
                    </div>
                    <div className="row">
                        <span className="bold gray2 col">
                            Your details as the personal representative
                        </span>
                    </div>
                </div>
            ) : (
                <></>
            ),
        [isBookingForChild],
    );

    const handleSelectChild = useCallback((index: number) => {
        setAccountPatientChildren((current) => {
            return current.map((child, i) => {
                if (i === index) {
                    return {
                        ...child,
                        isSelected: true,
                    };
                } else {
                    return {
                        ...child,
                        isSelected: false,
                    };
                }
            });
        });
    }, []);

    const handleSavingNewChild = useCallback((child: scheduling.patient.IPatientDetailsChild) => {
        setAddingChild(false);
        setAccountPatientChildren((current) => {
            if (isEmptyObject(current)) {
                return current.concat({ ...child, isSelected: true });
            } else {
                return current.concat(child);
            }
        });
    }, []);

    const handleSavingChildEdit = useCallback(
        (child: scheduling.patient.IPatientDetailsChild, index: number) => {
            setAccountPatientChildren((current) => {
                return current.map((c, i) => {
                    if (i === index) {
                        return child;
                    } else {
                        return c;
                    }
                });
            });
        },
        [],
    );

    const onSubmit = useCallback(
        async (e: BaseSyntheticEvent<any, any, any>) => {
            logDev("PatientSchedulingUserDetailsForm.onSubmit - stop propagation");
            e.preventDefault();
            e.stopPropagation();
            await handleSubmit(onSubmitNewPatient)(e);
        },
        [onSubmitNewPatient, handleSubmit],
    );

    const getParentRelationOptions = useCallback(() => {
        logDev("Getting patient relation options.");
        const route = `/${BASE_PUBLIC_API_ROUTE}/patient/relations/types?${ACCOUNT_PUBLIC_UUID}=${selfScheduleSettings?.accountPublicUuid}`;
        selfScheduleSettings?.accountPublicUuid &&
            getter(route)
                .then((response: AxiosResponse | void) => {
                    if (response?.status === 204) {
                        return;
                    }

                    const results = response && (response.data as string[]);
                    if (!results) {
                        notify({
                            level: "error",
                            title: "Error getting relationship names.",
                        });
                        return;
                    }

                    setRelationshipOptions(results.map(toOption) as ISelectOption[]);
                })
                .catch(handleError);
    }, [getter, selfScheduleSettings?.accountPublicUuid]);

    useEffect(() => {
        getParentRelationOptions();
    }, [getParentRelationOptions]);

    if (!selfSchedule || !selfScheduleSettings) {
        return null;
    }

    if (isLoading) {
        return (
            <FullScreenLoadingSpinner
                message={
                    userCondition === EUserCondition.NewUser
                        ? ["Creating your profile..."]
                        : ["Confirming your profile..."]
                }
            />
        );
    }

    return (
        <>
            <div className="row mb-2">
                <div className="col">
                    {title || (
                        <h3 className="extra-bold">
                            Add {patientFieldName} <br />
                            details:
                        </h3>
                    )}
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <LobbieCheckboxV2
                        checked={isBookingForChild}
                        onClick={handleSetBookingForChild}
                        label={`I'm a personal representative for the ${lowerPatientFieldName}`}
                    />
                </div>
            </div>
            {isBookingForChild && (
                <>
                    {" "}
                    <div className="row mb-2">
                        <div className="col">
                            {" "}
                            <h5 className="extra-bold">Select {lowerPatientFieldName}:</h5>{" "}
                        </div>
                    </div>
                    <div className="row mb-5">
                        <div className="col">
                            <PatientSchedulingUserDetailsChildren
                                options={relationshipOptions}
                                selectedChildIndex={selectedChildIndex}
                                handleUpdate={handleSavingChildEdit}
                                handleSelect={handleSelectChild}
                            >
                                {accountPatientChildren}
                            </PatientSchedulingUserDetailsChildren>
                            {isAddingChild ? (
                                <PatientSchedulingUserDetailsChildForm
                                    options={relationshipOptions}
                                    handleSavingNewChild={handleSavingNewChild}
                                    handleCancel={handleNotAddingChild}
                                />
                            ) : (
                                <Button
                                    variant="link"
                                    className="shadow-none bg-transparent border-0"
                                    onClick={handleAddingChild}
                                >
                                    Add {lowerPatientFieldName}
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            )}
            <Form onSubmit={onSubmit}>
                <div className="row">
                    <div className="col">
                        {createElement(NewPatientFormFields, {
                            control,
                            register,
                            getValues,
                            setValue,
                            errors,
                            searchPatientName: undefined,
                            isChildAccountPatient: false,
                            userType: EUserType.Patient,
                            title: representativeFormTitle,
                            patientSchedulingEmail: selfSchedule?.email,
                            patientSchedulingPhone: selfSchedule?.phone,
                            verificationMethod: selfScheduleSettings?.verificationMethod,
                        })}
                    </div>
                </div>

                <div className="row position-sticky fixed-bottom py-3 border-top bg-white">
                    {isBookingForChild && selectedChildIndex < 0 && (
                        <div className="mb-1">
                            Select the {lowerPatientFieldName}(s) you are representing for this
                            appointment.
                        </div>
                    )}
                    <div className="col text-right">
                        <Button
                            variant="primary pr-1 py-2 border-5"
                            type="submit"
                            disabled={isBookingForChild && selectedChildIndex < 0}
                        >
                            <span className="p-2">Continue</span>
                            <FiChevronRight
                                className="border-left ml-2 pl-1"
                                strokeWidth={"0.3em"}
                            />
                        </Button>
                    </div>
                </div>
            </Form>
        </>
    );
};

const getParentRelationshipName = (
    child: patients.IAccountPatientLite &
        patients.IParentAccountPatientLite &
        scheduling.patient.IPatientDetailsChild,
    parentAccountPatientId: number | undefined,
) => {
    return child?.parents
        ? child.parents.find((parent) => parent.id === parentAccountPatientId)
              ?.parentRelationshipName
        : "";
};

const getDefaultChildren = (accountPatient: patients.IAccountPatient | null) => {
    if (accountPatient?.children && !isEmptyObject(accountPatient.children)) {
        const updatedChildren = [] as scheduling.patient.IPatientDetailsChild[];
        (Array.isArray(accountPatient.children) ? accountPatient.children : []).forEach(
            (child, i) => {
                const parentRelationshipName = getParentRelationshipName(child, accountPatient.id);
                updatedChildren.push({
                    accountPatientId: child.accountPatientId,
                    firstName: child.firstName,
                    lastName: child.lastName,
                    isEdited: false,
                    isSelected: i === 0,
                    parentRelationshipName: parentRelationshipName || "",
                });
            },
        );
        return updatedChildren;
    } else {
        return [];
    }
};

export default PatientSchedulingUserDetailsForm;
